import { useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import MenuItem from "@mui/material/MenuItem";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { getColumns } from "./columnsData/columns";
import { useFieldArray, useFormContext } from "react-hook-form";
import { pairFields2 } from "../pairFields";
import {
  compactTableProps,
  smallerHeadCellProps,
} from "../../../../../../styles/muiProps";
import { computeShellUID } from "../../../../../../utils/pvUID";
import { Button, ListItemIcon, Box } from "@mui/material";

import {
  useResponsiveShellTable,
  getResponsiveShellTableConfig,
} from "./responsiveShellTable";

const INDEPENDENT_HEAD_DIAMETER_COMPONENTS = new Set([
  "Flat Rectangular Head (L/B)",
  "Flat Rectangular Head (R/T)",
  "Flat Round Head (L/B)",
  "Flat Round Head (R/T)",
]);

const hasIndependentHeadDiameter = (component) =>
  INDEPENDENT_HEAD_DIAMETER_COMPONENTS.has(component);

// Edge Mentor code labels for the Shell Input Data table.
// These match the code_label values in edge_mentor_training_content.json.
const SHELL_EDGE_MENTOR_CODE_LABELS = {
  UID: "shellUID",
  Component: "shellComponent",
  Diameter: "vesselDiameter",
  Material: "shellRowMaterial",
  "Can Length": "shellCanLength",
  "Long Seam": "shellLongSeam",
  "Provided Thk": "shellProvidedThickness",
  Weight: "Weight",
};

const SHELL_INPUT_COLUMN_WEIGHTS = {
  UID: 1,
  Component: 1.8,
  Diameter: 1,
  Material: 1.3,
  "Can Length": 1.05,
  "Long Seam": 1.1,
  "Provided Thk": 1.3,
  Weight: 0.85,
};

const SHELL_ACTION_COLUMN_WEIGHT = 0.8;

const InputTable = (props) => {
  const { reRender, isLoading } = props;
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "DataGridView5",
  });

  const rawColumns = useMemo(() => getColumns(getValues), [getValues]);

  const {
    viewportRef,
    tableWidth,
    headerFontSize,
    bodyFontSize,
    columns: responsiveColumns,
    extraWidth: actionsWidth,
  } = useResponsiveShellTable(
    rawColumns,
    SHELL_INPUT_COLUMN_WEIGHTS,
    SHELL_ACTION_COLUMN_WEIGHT,
  );

  const { mrtProps, headCellSx, bodyCellSx, editFieldSx } =
    getResponsiveShellTableConfig({
      tableWidth,
      headerFontSize,
      bodyFontSize,
    });

  //save cell changes in table
  const handleSaveCell = (cell, value) => {
    console.log(cell.column.id);
    // Normalise empty numeric cells to 0 so InvElectron validation never sees ""
    const numericColumns = [
      "Diameter",
      "Can Length",
      "Long Seam",
      "Provided Thk",
      "Weight",
    ];
    let finalValue = value;
    if (
      numericColumns.includes(cell.column.id) &&
      (value === "" || value === null || value === undefined)
    ) {
      finalValue = 0;
    }

    setValue(`DataGridView5.${cell.row.index}.${cell.column.id}`, finalValue);
    // Changes in Diameter applied throughout Table. And in the other tabs
    if (cell.column.id === "Diameter") {
      const currentComponent = getValues(
        `DataGridView5.${cell.row.index}.Component`,
      );

      // Flat round/rectangular heads keep their own diameter and do not force all rows.
      if (hasIndependentHeadDiameter(currentComponent)) {
        reRender();
        return;
      }

      fields.forEach((field, index) => {
        const rowComponent = getValues(`DataGridView5.${index}.Component`);
        if (!hasIndependentHeadDiameter(rowComponent)) {
          setValue(`DataGridView5.${index}.${cell.column.id}`, finalValue);
        }
      });

      pairFields2["Inside/Outside Diameter"].forEach((field, index) => {
        const isRegistered = Object.keys(errors).some(
          (error) => error === field,
        );
        if (
          !isRegistered &&
          (field.includes("txt") || field.includes("Text"))
        ) {
          register(field);
        }
        if (field.includes("txt") || field.includes("Text"))
          setValue(field, finalValue);
      });
      reRender();
    } else if (cell.column.id === "Provided Thk") {
      fields.forEach((field, index) => {
        setValue(`DataGridView5.${index}.${cell.column.id}`, finalValue);
      });
      setValue("txt_PT_CSC", finalValue);
      setValue("txt_PT_EHC", finalValue);
      setValue("txt_SPT_NC1", finalValue);
      reRender();
    } else if (cell.column.id === "Can Length") {
      // Can Length is per-row — heads and shells each keep their own value
      reRender();
    } else if (cell.column.id === "Component") {
      let material = "";
      if (value.includes("Head")) material = getValues("shellMaterial") || "";
      else material = getValues("headMaterial") || "";
      setValue(`DataGridView5.${cell.row.index}.Material`, material);
      // Auto-generate UID when component changes
      const projectTag = getValues("tagNo") || "";
      const uid = computeShellUID(value, projectTag);
      setValue(`DataGridView5.${cell.row.index}.UID`, uid);
      reRender();
    } else
      setValue(`DataGridView5.${cell.row.index}.${cell.column.id}`, finalValue);
  };

  //delete row
  const handleDeleteRow = (index) => {
    console.log({ index });
    remove(index);
    reRender();
  };

  //create new row in table
  const handleCreateNewRow = (values) => {
    append({
      UID: "",
      Component: "",
      Diameter: getValues("vesselDiameter") || 0,
      Material: "",
      "Can Length": 0,
      "Long Seam": 0,
      "Provided Thk": getValues("txt_PT_CSC") || 0,
      Weight: 0,
    });
  };

  return (
    <Box
      ref={viewportRef}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",

        overflowX: "hidden",
      }}
    >
      <MaterialReactTable
        {...compactTableProps}
        {...smallerHeadCellProps}
        {...mrtProps}
        columns={responsiveColumns ?? []}
        data={fields ?? []}
        editDisplayMode="table"
        enableEditing
        enableSorting={false}
        enableColumnActions={false}
        enableRowActions
        positionActionsColumn="last"
        enableTopToolbar={false}
        state={{
          isLoading,
        }}
        muiTableHeadCellProps={{
          sx: headCellSx,
        }}
        muiTableBodyCellProps={({ cell }) => ({
          "data-edge-mentor":
            SHELL_EDGE_MENTOR_CODE_LABELS[cell.column.id] || undefined,

          sx: bodyCellSx,
        })}
        muiEditTextFieldProps={({ cell }) => ({
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value);
          },

          fullWidth: true,

          sx: editFieldSx,
        })}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "Actions",

            size: actionsWidth,
            minSize: actionsWidth,
            maxSize: actionsWidth,

            grow: false,

            enableSorting: false,
            enableColumnActions: false,

            muiTableHeadCellProps: {
              sx: {
                ...headCellSx,

                width: `${actionsWidth}px !important`,
                minWidth: `${actionsWidth}px !important`,
                maxWidth: `${actionsWidth}px !important`,

                flex: `0 0 ${actionsWidth}px !important`,
                flexBasis: `${actionsWidth}px !important`,
              },
            },

            muiTableBodyCellProps: {
              sx: {
                ...bodyCellSx,

                width: `${actionsWidth}px !important`,
                minWidth: `${actionsWidth}px !important`,
                maxWidth: `${actionsWidth}px !important`,

                flex: `0 0 ${actionsWidth}px !important`,
                flexBasis: `${actionsWidth}px !important`,
              },
            },
          },
        }}
        renderBottomToolbarCustomActions={() => (
          <Button
            color="primary"
            size="small"
            onClick={() => handleCreateNewRow()}
            variant="contained"
          >
            Add row
          </Button>
        )}
        renderRowActionMenuItems={({ row }) => [
          <MenuItem
            key={0}
            sx={{ m: 0 }}
            onClick={() => handleDeleteRow(row.index)}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            Delete
          </MenuItem>,
        ]}
      />
    </Box>
  );
};

export default InputTable;
